<template>
	<ion-modal
		:is-open="showModal"
		:can-dismiss="!showModal"
		:backdrop-dismiss="false"
		:keyboard-close="false"
	>
		<ion-header>
			<ion-toolbar color="branding">
				<ion-title>{{ $t("login.title") }}</ion-title>
			</ion-toolbar>
		</ion-header>
		<ion-content class="ion-padding">
			<!-- Logo -->
			<div class="flex w-full justify-center p-6">
				<img
					class="h-32"
					style="filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.4))"
					:src="logo"
				/>
			</div>

			<template v-if="!loggingIn">
				<form
					id="login-form"
					class="mt-4 flex flex-col"
					@submit.prevent="login"
				>
					<!-- Username -->
					<ion-item>
						<ion-input
							v-model="username"
							:label="$t('login.username')"
							label-placement="floating"
							type="text"
							required
						></ion-input>
					</ion-item>

					<!-- Password -->
					<ion-item>
						<ion-input
							v-model="password"
							type="password"
							:label="$t('login.password')"
							label-placement="floating"
							required
							enterkeyhint="go"
						>
						</ion-input>
					</ion-item>

					<div class="mx-auto mb-4 mt-4">
						<ion-checkbox v-model="rememberMe">
							{{ $t("login.remember-me") }}
						</ion-checkbox>
					</div>

					<p
						v-if="errorMessage"
						class="mb-2 w-full text-center text-red-500"
					>
						{{ errorMessage }}
					</p>

					<!-- Login -->
					<ion-button
						expand="block"
						type="submit"
						:disabled="loggingIn || password.length < 4"
						>{{ $t("login.login") }}</ion-button
					>

					<!-- <ion-button expand="block" @click="webauthnLogin" :disabled="loggingIn" color="tertiary">
					<ion-icon slot="start" :icon="loggingIn ? syncicon : fingerprinticon"></ion-icon>
						{{ $t("login.device-login") }}
					</ion-button> -->

					<!-- Forgot Password -->
					<ForgotPasswordButton
						:disabled="loggingIn"
						:email="username"
					/>

					<ion-button
						v-if="showInstallButton"
						class="mx-auto mt-6 w-max"
						fill="outline"
						color="success"
						shape="round"
						@click="showInstallModal"
					>
						<ion-icon slot="start" :icon="download"></ion-icon>
						{{ $t("login.install-app") }}
					</ion-button>
				</form>
				<section class="flex w-full justify-center text-gray-500">
					<LanguageSelector visible />
				</section>
			</template>
			<template v-else>
				<div
					class="flex w-full flex-row items-center justify-center p-16"
				>
					<div class="flex flex-col items-center gap-3">
						<div>{{ $t("login.logging-in") }}</div>
						<ion-spinner color="success" name="circular" />
						<div
							v-if="showTimeoutMessage"
							class="max-w-96 text-wrap text-center italic text-red-600 opacity-75"
						>
							{{ $t("login.timeout-message") }}
						</div>
					</div>
				</div>
			</template>
		</ion-content>
	</ion-modal>
</template>

<script lang="ts" setup>
	import seedgreenAPIAnonymous from "@/utils/seedgreen-api-anonymous";
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonIcon,
		IonSpinner,
		IonContent,
		IonTitle,
		IonItem,
		IonInput,
		IonButton,
		IonCheckbox,
	} from "@ionic/vue";
	import { AxiosError } from "axios";
	import { useVerifyToken } from "seedgreen-shared/queries/verify-token";
	import { computed, inject, ref } from "vue";
	import { useI18n } from "vue-i18n";
	import ForgotPasswordButton from "@/components/feature/auth/ForgotPasswordButton.vue";
	import { download } from "ionicons/icons";
	import { useShowInstallButton } from "@/composables/useShowInstallButton";
	import { usePostHog } from "@/composables/usePosthog";
	import LanguageSelector from "@/components/ui/LanguageSelector.vue";
	import { LoginRequest } from "seedgreen-shared/dtos/LoginRequest";

	const showInstallModal = inject("showInstallModal") as () => void;
	const showInstallButton = useShowInstallButton();
	const { t } = useI18n();
	const { posthog } = usePostHog();

	const {
		tokenValid,
		refetch: refetchTokenValid,
		isFetching: isCheckingToken,
	} = useVerifyToken();

	const showModal = computed(
		() => !isCheckingToken.value && !tokenValid.value,
	);

	const username = ref("");
	const password = ref("");
	const rememberMe = ref(true);
	const errorMessage = ref("");
	const loggingIn = ref(false);
	const logo = ref(localStorage.m_logo || "/images/branding/logo-color.png");

	// Timeout message shows if login takes too long
	const LOGIN_TIMEOUT_MESSAGE_MS = 10 * 1000;
	const showTimeoutMessage = ref(false);
	let timeout: NodeJS.Timeout | undefined = undefined;
	function startLoginTimeout() {
		clearTimeout(timeout);
		showTimeoutMessage.value = false;
		timeout = setTimeout(() => {
			showTimeoutMessage.value = true;
		}, LOGIN_TIMEOUT_MESSAGE_MS);
	}
	function clearLoginTimeout() {
		showTimeoutMessage.value = false;
		clearTimeout(timeout);
	}

	async function login() {
		loggingIn.value = true;
		errorMessage.value = "";
		startLoginTimeout();

		const loginMetrics = {
			method: "email&password",
			username: username.value,
		};

		posthog.capture("login:login_attempt", loginMetrics);

		try {
			const loginRequest: LoginRequest = {
				Username: username.value,
				Password: password.value,
				RememberMe: rememberMe.value,
			};
			await seedgreenAPIAnonymous.post("v2/auth/login", loginRequest, {
				headers: {
					"Content-Type": "application/json",
				},
			});
			posthog.capture("login:login_success", loginMetrics);
		} catch (error) {
			if (error instanceof AxiosError && error.status === 401) {
				posthog.capture("login:login_fail", {
					...loginMetrics,
					reason: "invalid_credentials",
				});
				errorMessage.value = t("login.error-incorrect-user-pw");
			} else if (error instanceof AxiosError && error.status === 403) {
				posthog.capture("login:login_fail", {
					...loginMetrics,
					reason: "unauthorized",
				});
				errorMessage.value = t("login.error-unavailable");
			} else {
				posthog.capture("login:login_fail", {
					...loginMetrics,
					reason: "unknown",
				});
				errorMessage.value = t("login.error-unknown");
				// TODO: Sentry capture
			}
			loggingIn.value = false;
			return;
		}

		try {
			await refetchTokenValid();
		} finally {
			loggingIn.value = false;
			clearLoginTimeout();
		}
	}
</script>
